import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { billingService } from '../services/billingService'
import type { BillableItem, BillingPosItem } from '../types/billing'
import './ConsultationBillingItems.css'

interface ConsultationBillingItemsProps {
  consultationId: string
  patientId: string
}

export function ConsultationBillingItems({ consultationId, patientId }: ConsultationBillingItemsProps) {
  const queryClient = useQueryClient()
  const [selectedItemId, setSelectedItemId] = useState('')
  const [description, setDescription] = useState('')
  const [amount, setAmount] = useState('')
  const [provider, setProvider] = useState<'manual' | 'stripe' | 'square'>('manual')

  const { data: items = [], isLoading } = useQuery<BillableItem[]>({
    queryKey: ['billing-items', 'consultation', consultationId],
    queryFn: () => billingService.getItemsByConsultation(consultationId),
    enabled: !!consultationId,
  })
  const { data: posItems = [], isLoading: isLoadingPosItems } = useQuery<BillingPosItem[]>({
    queryKey: ['billing-pos-items'],
    queryFn: () => billingService.getPosItems(),
  })
  const currency = items[0]?.currency || 'EUR'
  const totalAmount = items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0)
  const formatMoney = (value: number) =>
    new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)

  const createItemMutation = useMutation({
    mutationFn: () => billingService.createConsultationItem(consultationId, {
      patientId,
      description,
      amount: Number(amount),
      provider,
    }),
    onSuccess: () => {
      setDescription('')
      setAmount('')
      queryClient.invalidateQueries({ queryKey: ['billing-items', 'consultation', consultationId] })
    },
  })

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!selectedItemId || !description.trim() || !amount) {
      return
    }
    createItemMutation.mutate()
  }

  return (
    <div className="consultation-billing">
      <div className="consultation-billing-header">
        <div>
          <h3>Billing Items</h3>
          <p className="consultation-billing-subtitle">Add charges tied to this consultation.</p>
        </div>
        <div className="consultation-billing-metrics">
          <div>
            <span>Items</span>
            <strong>{items.length}</strong>
          </div>
          <div>
            <span>Total</span>
            <strong>{formatMoney(totalAmount)}</strong>
          </div>
        </div>
      </div>
      <form className="consultation-billing-form" onSubmit={handleSubmit}>
        <label className="consultation-billing-field">
          <span>POS Item</span>
          <select
            value={selectedItemId}
            onChange={(event) => {
              const itemId = event.target.value
              setSelectedItemId(itemId)
              const selected = posItems.find((item) => item.id === itemId)
              if (selected) {
                setDescription(selected.label)
                setAmount(String(selected.amount))
              } else {
                setDescription('')
                setAmount('')
              }
            }}
            required
            disabled={isLoadingPosItems}
          >
            <option value="">{isLoadingPosItems ? 'Loading POS items...' : 'Select POS item'}</option>
            {posItems.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label} • {new Intl.NumberFormat('en-IE', { style: 'currency', currency: item.currency }).format(item.amount)}
              </option>
            ))}
          </select>
        </label>
        <label className="consultation-billing-field">
          <span>Amount</span>
          <input
            type="number"
            step="0.01"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            placeholder="0.00"
            required
            readOnly
          />
        </label>
        <label className="consultation-billing-field">
          <span>Provider</span>
          <select value={provider} onChange={(event) => setProvider(event.target.value as 'manual' | 'stripe' | 'square')}>
            <option value="manual">Manual</option>
            <option value="stripe">Stripe</option>
            <option value="square">Square</option>
          </select>
        </label>
        <button className="btn-primary" type="submit" disabled={createItemMutation.isPending}>
          {createItemMutation.isPending ? 'Adding...' : 'Add Item'}
        </button>
      </form>

      <div className="consultation-billing-list">
        {isLoading ? (
          <p className="consultation-billing-muted">Loading items...</p>
        ) : items.length === 0 ? (
          <p className="consultation-billing-muted">No billing items added yet.</p>
        ) : (
          <div className="consultation-billing-table">
            <div className="consultation-billing-row consultation-billing-row-header">
              <span>Description</span>
              <span>Amount</span>
              <span>Status</span>
            </div>
            {items.map((item) => (
              <div key={item.id} className="consultation-billing-row">
                <span className="billing-item-description">{item.description}</span>
                <span className="billing-item-amount">
                  {new Intl.NumberFormat('en-IE', { style: 'currency', currency: item.currency }).format(item.amount)}
                </span>
                <span className="billing-item-status">{item.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
